import { useQuery } from '@tanstack/react-query';
import { Form, SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';

import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
// Mejora: se extrajo la lógica duplicada de autocompletar empresa desde ticket a useTicketCompanyAutoFill
import { useTicketCompanyAutoFill } from '@/hooks/useTicketCompanyAutoFill';
import CompanyContact from '@/model/General/CompanyContact';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';
// Mejora: se reemplazó el template literal duplicado por formatTicketLabel centralizado
import { formatTicketLabel } from '@/utils/formHelpers';

export const useReceptionForm = (formProps: FormProps, initialValuesModified, edit?: boolean) => {
  const { form } = formProps;
  const { initialValuesSetted } = useFormInitialSet(
    form,
    initialValuesModified,
    [
      { watchField: 'company', resetFields: ['company_contact'] },
      { watchField: 'ticket', resetFields: ['company_contact'] },
    ],
    {
      withoutInitial: !edit && !initialValuesModified?.ticket,
    },
  );
  const { companySelectProps, shippersSelectProps, ticketsSelectProps } = useOutletContext<{
    companySelectProps: SelectProps;
    shippersSelectProps: SelectProps;
    ticketsSelectProps: SelectProps;
  }>();
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps,
    resource: 'empresas',
    initialValue: initialValuesModified?.company,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const shippersSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: shippersSelectProps,
    resource: RESOURCES.SHIPPERS,
    initialValue: initialValuesModified?.shipper,
    labelKey: 'name',
    valueKey: 'id',
  });
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: initialValuesModified?.ticket,
    labelKey: formatTicketLabel,
    valueKey: 'id',
  });
  /**
   * Autocompletar empresa desde ticket (usa hook genérico).
   */
  const {
    ticketsSelectProps: ticketsSelectPropsModified,
    companySelectProps: companySelectPropsModified,
    ticketCompanyOption,
    movilLink,
  } = useTicketCompanyAutoFill({
    form,
    ticketsSelectProps: ticketsSelectPropsWithInitials,
    companySelectProps: companySelectPropsWithInitials,
    initialTicketId: initialValuesModified?.ticket,
    edit,
  });
  // Contactos para envio de presupuesto
  const company = Form.useWatch('company', form);
  const { companyRepository } = useRepositoriesContext();
  const ticketCompanyId = ticketCompanyOption[0]?.value as number | undefined;
  const selectedCompanyId = company ?? (!edit ? ticketCompanyId : undefined);
  const shouldFetchSelectedCompany = typeof selectedCompanyId === 'number';
  const shouldFetchTicketCompany =
    typeof ticketCompanyId === 'number' && ticketCompanyId !== selectedCompanyId;
  // Implementación TanStack Query para contactos de empresa
  const { data: selectedCompanyContacts, isFetching: loadingSelectedCompanyContacts } = useQuery({
    queryKey: ['companyContacts', selectedCompanyId],
    queryFn: () => companyRepository.getCompanyContact(selectedCompanyId as number),
    enabled: shouldFetchSelectedCompany,
  });
  const { data: ticketCompanyContacts, isFetching: loadingTicketCompanyContacts } = useQuery({
    queryKey: ['companyContacts', ticketCompanyId],
    queryFn: () => companyRepository.getCompanyContact(ticketCompanyId!),
    enabled: shouldFetchTicketCompany,
  });
  const companyContactData = useMemo<{
    selectedCompany?: CompanyContact[];
    ticketCompany?: CompanyContact[];
  }>(
    () => ({
      selectedCompany: shouldFetchSelectedCompany ? (selectedCompanyContacts ?? []) : undefined,
      ticketCompany: shouldFetchTicketCompany ? (ticketCompanyContacts ?? []) : undefined,
    }),
    [
      selectedCompanyContacts,
      shouldFetchSelectedCompany,
      ticketCompanyContacts,
      shouldFetchTicketCompany,
    ],
  );
  const loadingCompanyContactData = loadingSelectedCompanyContacts || loadingTicketCompanyContacts;
  const [sendToClient, setSendToClient] = useState(
    formProps.initialValues?.company_contact ? true : false,
  );

  return {
    initialValuesSetted,
    companySelectProps: companySelectPropsModified,
    shippersSelectProps: shippersSelectPropsWithInitials,
    ticketsSelectProps: ticketsSelectPropsModified,
    // Contactos para envio de presupuesto
    companyContactData,
    loadingCompanyContactData,
    sendToClient,
    setSendToClient,
    // link al movil
    movilLink,
  };
};
